import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Ticket, RefreshCcw, Search, Filter, MessageSquare, Send,
  Clock, CheckCircle, AlertTriangle, Building2, User, ChevronLeft
} from 'lucide-react';

const PlatformSupportPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      
      const response = await api.get(`/super-admin/tickets?${params}`);
      setTickets(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority]);

  const fetchTicketDetails = useCallback(async (id) => {
    try {
      const response = await api.get(`/super-admin/tickets/${id}`);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du ticket');
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails(ticketId);
    } else {
      setSelectedTicket(null);
    }
  }, [ticketId, fetchTicketDetails]);

  const handleSendResponse = async () => {
    if (!responseText.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      await api.post(`/super-admin/tickets/${selectedTicket.id}/respond`, {
        message: responseText
      });
      toast.success('Réponse envoyée');
      setResponseText('');
      fetchTicketDetails(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedTicket) return;
    
    try {
      await api.put(`/super-admin/tickets/${selectedTicket.id}/status`, { status });
      toast.success('Statut mis à jour');
      fetchTicketDetails(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { label: 'Ouvert', className: 'bg-blue-100 text-blue-700' },
      in_progress: { label: 'En cours', className: 'bg-yellow-100 text-yellow-700' },
      waiting_response: { label: 'Attente réponse', className: 'bg-orange-100 text-orange-700' },
      resolved: { label: 'Résolu', className: 'bg-emerald-100 text-emerald-700' },
      closed: { label: 'Fermé', className: 'bg-slate-100 text-slate-700' }
    };
    const c = config[status] || config.open;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Basse', className: 'bg-slate-100 text-slate-600' },
      medium: { label: 'Moyenne', className: 'bg-blue-100 text-blue-600' },
      high: { label: 'Haute', className: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Urgent', className: 'bg-red-100 text-red-600' }
    };
    const c = config[priority] || config.medium;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  if (selectedTicket) {
    return (
      <div className="p-6 space-y-6" data-testid="ticket-detail">
        <Button variant="ghost" onClick={() => navigate('/admin/platform-support')}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Retour aux tickets
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl">{selectedTicket.subject}</CardTitle>
                <CardDescription className="flex items-center gap-4 mt-2">
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    {selectedTicket.organization?.name}
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {selectedTicket.created_by?.name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(selectedTicket.created_at).toLocaleString()}
                  </span>
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedTicket.status)}
                {getPriorityBadge(selectedTicket.priority)}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                {selectedTicket.description}
              </p>
            </div>

            <div className="flex gap-2">
              <Select onValueChange={handleUpdateStatus} value={selectedTicket.status}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Changer le statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Ouvert</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="waiting_response">Attente réponse</SelectItem>
                  <SelectItem value="resolved">Résolu</SelectItem>
                  <SelectItem value="closed">Fermé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Conversation ({selectedTicket.responses?.length || 0})
              </h3>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {selectedTicket.responses?.map(response => (
                  <div
                    key={response.id}
                    className={`p-4 rounded-lg ${
                      response.is_from_super_admin
                        ? 'bg-violet-50 dark:bg-violet-900/20 ml-8'
                        : 'bg-slate-50 dark:bg-slate-800 mr-8'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">
                        {response.is_from_super_admin ? 'Super Admin' : response.user?.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(response.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                      {response.message}
                    </p>
                  </div>
                ))}
              </div>

              {selectedTicket.status !== 'closed' && (
                <div className="space-y-3 pt-4 border-t">
                  <Textarea
                    placeholder="Votre réponse..."
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={4}
                  />
                  <Button 
                    onClick={handleSendResponse} 
                    disabled={sending || !responseText.trim()}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? 'Envoi...' : 'Envoyer la réponse'}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="platform-support-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onMenuClick} className="md:hidden p-2 -ml-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800" aria-label="Ouvrir le menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Support Plateforme
            </h1>
            <p className="text-slate-500">Tickets des CFA Admin</p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="open">Ouverts</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="waiting_response">Attente réponse</SelectItem>
                <SelectItem value="resolved">Résolus</SelectItem>
                <SelectItem value="closed">Fermés</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">Haute</SelectItem>
                <SelectItem value="medium">Moyenne</SelectItem>
                <SelectItem value="low">Basse</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchTickets}>
              <RefreshCcw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
            <p className="text-slate-500">Aucun ticket en attente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map(ticket => (
            <Card 
              key={ticket.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/admin/platform-support/${ticket.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-slate-900 dark:text-white">
                        {ticket.subject}
                      </h3>
                      {getStatusBadge(ticket.status)}
                      {getPriorityBadge(ticket.priority)}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {ticket.organization?.name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        {ticket.response_count} réponse(s)
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlatformSupportPage;
